import { useEffect, useState } from 'react'
import logo from '../assets/logo.png'
import { Icono } from '../componentes/Icono.js'
import { olvideMiClave } from '../datos/credenciales.js'
import { cuentasDeDemostracion, obtenerRepositorio } from '../datos/index.js'
import type { Jugador } from '../datos/index.js'
import { ir } from '../navegacion.js'

/**
 * La puerta de entrada.
 *
 * El nombre de usuario es la llave, igual que en el resto del sistema: quien no
 * tiene uno cargado existe, juega y acumula historial, pero no puede entrar acá.
 * Es la regla que vive en `estadoDeCuenta` de `packages/schema`, y por eso el
 * error de «no hay ninguna cuenta con ese usuario» no es un caso raro sino el
 * más común de todos: la mayoría del club juega y nunca pidió una contraseña.
 *
 * No es el email porque el servidor atiende a varios clubes y el email se
 * repite. El club no se pregunta: sale de la ficha de quien entra.
 *
 * Mientras no haya API, cualquier contraseña entra. La pantalla lo dice en voz
 * alta en vez de simularlo en silencio: una demo que finge autenticar es una que
 * el día que se conecte de verdad va a fallar en el único lugar donde nadie
 * estaba mirando.
 */
export function Ingreso({ alIngresar }: { alIngresar: (jugador: Jugador) => void }) {
  // En desarrollo arranca con una cuenta de las que deja `sembrar` para probar
  // sin tipear; en el build de producción los campos quedan vacíos.
  const [usuario, setUsuario] = useState(import.meta.env.DEV ? 'marta.quiroga' : '')
  const [password, setPassword] = useState(import.meta.env.DEV ? 'domino-1234' : '')
  const [verPassword, setVerPassword] = useState(false)
  /* «Olvidé mi contraseña» vive en la misma tarjeta: null es el ingreso normal,
     y con texto es la respuesta del servidor ya mostrada. */
  const [olvido, setOlvido] = useState<'pidiendo' | 'enviado' | null>(null)
  const [respuestaOlvido, setRespuestaOlvido] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)
  const [cuentas, setCuentas] = useState<{ nombre: string; usuario: string }[]>([])

  useEffect(() => {
    setCuentas(cuentasDeDemostracion())
  }, [])

  async function pedirEnlace(e: React.FormEvent) {
    e.preventDefault()
    setEnviando(true)
    setError(null)
    try {
      setRespuestaOlvido(await olvideMiClave(usuario))
      setOlvido('enviado')
    } catch (problema) {
      setError(problema instanceof Error ? problema.message : String(problema))
    } finally {
      setEnviando(false)
    }
  }

  async function enviar(e: React.FormEvent) {
    e.preventDefault()
    setEnviando(true)
    setError(null)
    try {
      const repo = await obtenerRepositorio()
      alIngresar(await repo.ingresar(usuario, password))
    } catch (problema) {
      setError(problema instanceof Error ? problema.message : String(problema))
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div className="ingreso">
      <div className="ingreso-marca">
        <img src={logo} alt="" width={76} height={76} className="ingreso-logo" />
        <h1>DomiRoyal</h1>
        <p>Mira tus torneos, tu mesa y tu posición.</p>
      </div>

      {olvido && (
        <form className="tarjeta ingreso-form" onSubmit={pedirEnlace}>
          {olvido === 'pidiendo' ? (
            <>
              <p className="ingreso-explica">
                Escribe tu usuario y te mandamos al email un enlace para elegir una contraseña nueva.
              </p>
              <label className="ingreso-campo">
                <span>Usuario</span>
                <div className="ingreso-entrada">
                  <Icono nombre="persona" tamano={19} />
                  <input
                    value={usuario}
                    onChange={(e) => setUsuario(e.target.value)}
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    enterKeyHint="send"
                    placeholder="tu usuario"
                    required
                    autoFocus
                  />
                </div>
              </label>
              {error && (
                <p className="aviso error" role="alert">
                  {error}
                </p>
              )}
              <button className="boton principal ingreso-entrar" type="submit" disabled={enviando}>
                {enviando ? 'Enviando…' : 'Mandarme el enlace'}
              </button>
            </>
          ) : (
            <p className="aviso ok" role="status">
              {respuestaOlvido} Si no te llega, pídele a quien organiza en tu club que te lo mande.
            </p>
          )}
          <button
            type="button"
            className="ingreso-enlace"
            onClick={() => {
              setOlvido(null)
              setError(null)
            }}
          >
            Volver a entrar
          </button>
        </form>
      )}

      {!olvido && (
        <form className="tarjeta ingreso-form" onSubmit={enviar}>
          <label className="ingreso-campo">
            <span>Usuario</span>
            <div className="ingreso-entrada">
              <Icono nombre="persona" tamano={19} />
              <input
                value={usuario}
                onChange={(e) => setUsuario(e.target.value)}
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="next"
                placeholder="tu usuario"
                required
              />
            </div>
          </label>

          <label className="ingreso-campo">
            <span>Contraseña</span>
            <div className="ingreso-entrada">
              <Icono nombre="candado" tamano={19} />
              <input
                type={verPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                enterKeyHint="go"
                placeholder="tu contraseña"
                required
              />
              <button
                type="button"
                className="ingreso-ver"
                onClick={() => setVerPassword((v) => !v)}
                aria-pressed={verPassword}
              >
                {verPassword ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>
          </label>

          {error && (
            <p className="aviso error" role="alert">
              {error}
            </p>
          )}

          <button className="boton principal ingreso-entrar" type="submit" disabled={enviando}>
            {enviando ? 'Entrando…' : 'Entrar'}
          </button>

          {/* La mayoría del club juega sin cuenta: el que llega acá sin usuario
              tiene que saber a quién pedírselo, no quedarse probando. */}
          <button
            type="button"
            className="ingreso-enlace"
            onClick={() => {
              setOlvido('pidiendo')
              setError(null)
            }}
          >
            ¿Olvidaste tu contraseña?
          </button>

          <p className="ingreso-ayuda">¿No tienes usuario? Pídeselo a quien organiza en tu club.</p>
        </form>
      )}

      {/* La salida a la vitrina, para el que llegó hasta acá sin cuenta: mirar
          el torneo no pide usuario. */}
      <button type="button" className="ingreso-vitrina" onClick={() => ir({ nombre: 'vivo' })}>
        <Icono nombre="vivo" tamano={18} />
        Ver los torneos en vivo, sin cuenta
        <Icono nombre="flecha" tamano={16} />
      </button>

      {/* Con cuentas de mentira, el que abre la demo no tiene forma de adivinar
          un usuario válido. Tocar una lo escribe: no es un atajo de desarrollo,
          es lo único que hace usable una pantalla que pide una llave que nadie
          tiene. */}
      {cuentas.length > 0 && (
        <section className="tarjeta demo-cuentas">
          <h2>Modo demostración</h2>
          <p className="tenue">
            Los datos son de mentira y cualquier contraseña sirve. Toca un nombre para probar con
            su cuenta:
          </p>
          <ul>
            {cuentas.map((c) => (
              <li key={c.usuario}>
                <button
                  type="button"
                  onClick={() => {
                    setUsuario(c.usuario)
                    setPassword('domino')
                  }}
                >
                  <b>{c.nombre}</b>
                  <span className="tenue">{c.usuario}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}
